import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Logger } from '@saviynt/common';
import {
  ApplicationIcon,
  Avatar,
  Box,
  ButtonIcon,
  ButtonSelect,
  type FloatingPlacements,
  Icon,
  Illustration,
  Menu,
  type MenuOption,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  CF_CRT_MED,
  CF_NEU_BLD,
  CF_NEU_MED,
  CF_NEU_STL,
  CF_SEC_BLD,
  CF_SEC_MED,
  LIMIT_INLINE_COUNT,
  testIds,
} from '../../../constants';
import { useEffectAfterMount, useInViewObserver } from '../../../hooks';
import { WRAPPER_MODE } from '../../UiWrapper';
import {
  DEFAULT_PAGINATION,
  MAX_COUNT,
  OWNERS_RANK_OPTIONS,
  OWNERS_TYPE,
} from '../constants';
import { ListControls, ListSidePanel, ListSkeleton } from '../Shared';
import {
  type AccountOwnerListResponse,
  type CustomListModalTypes,
  type EndpointParams,
} from '../types';
import { getKeyValue, getOwnersType } from '../utils';

import './AccountOwners.css';

const MAX_OWNER_COUNT = 10;

interface TrackedListItem extends AccountOwnerListResponse {
  tracker: {
    id: number;
    isChecked: boolean;
    type: (typeof OWNERS_TYPE)[keyof typeof OWNERS_TYPE];
    rank: string;
  };
}

interface AccountOwnerListItemProps {
  isFullView: boolean;
  listItem: TrackedListItem;
  assignedCount?: number;
  isAssignedList: boolean;
  isLoading: boolean;
  handleClick: (id: number, type: string) => void;
  handleRankChange: (id: number, type: string, rank: string) => void;
  handleRowClick: (id: number, type: string) => void;
}

/**
 * A single row in the account owners list, displaying owner info with
 * add/remove and rank controls.
 *
 * @param props - {@link AccountOwnerListItemProps}
 * @returns The rendered list item button element.
 */
function AccountOwnerListItem({
  isFullView,
  listItem,
  assignedCount,
  isAssignedList,
  isLoading,
  handleClick,
  handleRankChange,
  handleRowClick,
}: AccountOwnerListItemProps) {
  const {
    firstname,
    lastname,
    displayname,
    username,
    description,
    user_groupname: userGroupName,
    users,
    tracker: { id, isChecked, type, rank },
  } = listItem;

  const isLimit = Boolean(
    assignedCount !== undefined && assignedCount >= MAX_OWNER_COUNT
  );
  const isDisabled = useMemo(() => {
    if ((isLimit && !isChecked) || isLoading) {
      return true;
    }

    return false;
  }, [isChecked, isLimit, isLoading]);

  // Utils
  const getToolTipText = () => {
    if (isDisabled) {
      return 'Max number of owners has been reached. Remove one from the Assigned list before adding more.';
    }

    if (isAssignedList || isChecked) {
      return 'Remove';
    }

    return 'Add';
  };

  const handleItemClick = useCallback(() => {
    if (isDisabled) {
      return;
    }

    handleClick(id, type);
  }, [handleClick, isDisabled, id, type]);

  const handleRowPanel = useCallback(() => {
    handleRowClick(id, type);
  }, [handleRowClick, id, type]);

  const tooltipConfigs = useMemo(() => {
    const config = { text: '', placement: 'bottom' as FloatingPlacements };

    if (type === OWNERS_TYPE.MACHINE) {
      config.text = description ?? '-';
    }

    if (type === OWNERS_TYPE.USERGROUP) {
      config.text = `${users.length} members`;
    }

    if (type === OWNERS_TYPE.USER) {
      config.text = `${firstname} ${lastname} (${username})`;
    }

    return config;
  }, [description, firstname, lastname, type, username, users]);

  return (
    <button
      type='button'
      aria-disabled='false'
      tabIndex={isDisabled ? -1 : 0}
      onClick={() => {
        if (isFullView) {
          handleRowPanel();
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.detail === 1 && isFullView) {
          handleRowPanel();
        }
      }}
      className={cx(
        'AccountOwnerListItem',
        isChecked && 'AccountOwnerListItem--isChecked',
        isDisabled && 'AccountOwnerListItem--isDisabled',
        isAssignedList && 'AccountOwnerListItem--isAssignedList'
      )}>
      <Box tag='div' className='AccountOwnerListItem-iconWrapper'>
        {type === OWNERS_TYPE.MACHINE && (
          <ApplicationIcon size='medium' icon='MachineIdentity' />
        )}
        {type === OWNERS_TYPE.USERGROUP && (
          <ApplicationIcon size='medium' icon='Group' />
        )}
        {type === OWNERS_TYPE.USER && (
          <ApplicationIcon size='medium' icon='User' />
        )}
      </Box>
      <Box tag='div' className='AccountOwnerListItem-contentWrapper'>
        <Typography kind='body3' color={CF_SEC_MED} lineLimit={1}>
          {type === OWNERS_TYPE.MACHINE && (
            <FormattedMessage
              id='3I9vgt'
              defaultMessage='Machine'
              description='Machine label in list of account owners'
            />
          )}
          {type === OWNERS_TYPE.USERGROUP && (
            <FormattedMessage
              id='lGcO12'
              defaultMessage='User Group'
              description='User Group'
            />
          )}
          {type === OWNERS_TYPE.USER && (
            <FormattedMessage
              id='ZFVRDj'
              defaultMessage='User'
              description='User label in list of account owners'
            />
          )}
        </Typography>
        <Typography kind='h4' color={CF_NEU_BLD} lineLimit={1}>
          {type === OWNERS_TYPE.MACHINE && (displayname ?? username)}
          {type === OWNERS_TYPE.USERGROUP && userGroupName}
          {type === OWNERS_TYPE.USER && (displayname ?? username)}
        </Typography>
        <Typography
          kind='body2'
          color={CF_NEU_STL}
          lineLimit={1}
          tooltipConfigs={tooltipConfigs}
          showTooltipOnHover>
          {type === OWNERS_TYPE.MACHINE &&
            (description ?? (
              <FormattedMessage
                id='YoUJI1'
                defaultMessage='-'
                description='Hyphen'
              />
            ))}
          {type === OWNERS_TYPE.USERGROUP && (
            <FormattedMessage
              id='5QyoDZ'
              defaultMessage='{userCount} members'
              description='User Group label in list of account owners'
              values={{
                userCount: users.length,
              }}
            />
          )}
          {type === OWNERS_TYPE.USER && (
            <FormattedMessage
              id='EH/So1'
              defaultMessage='{firstname} {lastname} ({username})'
              description='first name, last name and username'
              values={{
                firstname,
                lastname,
                username,
              }}
            />
          )}
        </Typography>
      </Box>
      <Box
        tag='div'
        className={cx(
          'AccountOwnerListItem-selectWrapper'
          // isFullView && 'AccountOwnerListItem-selectWrapper--isFullView'
        )}>
        {(isAssignedList || !isFullView) && (
          <Box tag='div' className='AccountOwnerListItem-rankCell'>
            <Typography kind='body2' color={CF_NEU_BLD}>
              <FormattedMessage
                id='do+b0S'
                defaultMessage='Rank:'
                description='Rank'
              />
            </Typography>
            <Menu
              isPortalEnabled
              options={OWNERS_RANK_OPTIONS.map((r) => ({
                ...r,
                isChecked: r.value === rank,
              }))}
              minOptionsForSearch={MAX_COUNT + 1}
              onChange={(selectedOption: MenuOption) => {
                handleRankChange(id, type, selectedOption.value);
              }}
              trigger={
                <ButtonSelect
                  kind='rectangle'
                  label={
                    OWNERS_RANK_OPTIONS.find((r) => r.value === rank)?.label ??
                    rank
                  }
                  size='small'
                  className='AccountOwnerListItem-rankCellButton'
                />
              }
              size='small'
            />
          </Box>
        )}
        <Tooltip
          trigger={
            <ButtonIcon
              kind='outlinedSecondary'
              isSelected={isChecked && !isAssignedList}
              isDisabled={isDisabled}
              icon={
                <Icon
                  kind={isAssignedList || isChecked ? 'Subtract' : 'Add'}
                  size='xSmall'
                />
              }
              ariaLabel={isAssignedList || isChecked ? 'Remove' : 'Add'}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();

                handleItemClick();
              }}
              tabIndex={0}
              dataTestId={`${
                testIds.LIST_VIEWER_ACCOUNT_OWNERS_ITEM_BTN
              }-${id}-${type.toUpperCase()}`}
            />
          }
          text={getToolTipText()}
          placement='left'
        />
      </Box>
    </button>
  );
}

AccountOwnerListItem.defaultProps = {
  assignedCount: undefined,
};

const DEFAULT_LIST_STATE = {
  currentOffset: 0,
  totalCount: null as number | null,
  isObserverInView: true,
  isListError: false,
  isListLoading: false,
};

const DEFAULT_SIDEPANEL_STATE = {
  activeIndex: null,
  activeItem: null,
};

interface AccountOwnersProps {
  isFullView: boolean;
  retentions?: AccountOwnerListResponse[];
  fetchList: (pagination?: EndpointParams) => Promise<unknown>;
  setChecked: React.Dispatch<React.SetStateAction<CustomListModalTypes[]>>;
  currentMode: string;
  setInlineItemCount: React.Dispatch<React.SetStateAction<number | null>>;
}

/**
 * Paginated account owners list with search, type filtering, assignment
 * toggling, rank selection, and a detail side panel.
 *
 * @param props - {@link AccountOwnersProps}
 * @returns The rendered account owners list with optional side panel.
 */
function AccountOwners({
  isFullView,
  retentions,
  fetchList,
  setChecked,
  currentMode,
  setInlineItemCount,
}: AccountOwnersProps) {
  const [listControls, setListControls] = useState({
    searchText: '',
  });
  const [listState, setListState] = useState(DEFAULT_LIST_STATE);
  const [listRows, setListRows] = useState<TrackedListItem[]>(
    [] as TrackedListItem[]
  );
  const [ownerType, setOwnerType] = useState(1);
  const [assignType, setAssignType] = useState(1);
  const [activeRows, setActiveRows] = useState<TrackedListItem[]>(
    retentions?.map((item) => ({
      ...item,
      tracker: {
        id: item.id,
        type: item.type,
        isChecked: true,
        rank: String(item.rank ?? '1'),
      },
    })) ?? []
  );
  // SidePanel
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [sidePanelContext, setSidePanelContext] = useState<{
    activeIndex: number | null;
    activeItem: TrackedListItem | null;
  }>(DEFAULT_SIDEPANEL_STATE);

  // Constants
  const focusedList = useMemo(() => {
    if (assignType === 2) {
      return activeRows;
    }

    return listRows;
  }, [activeRows, assignType, listRows]);
  const isValidApiCall = useMemo(
    () =>
      Boolean(
        !listState.isListLoading &&
          (listState.totalCount === null ||
            (listState.currentOffset < listState.totalCount &&
              listRows.length < listState.totalCount))
      ),
    [
      listState.currentOffset,
      listState.totalCount,
      listRows.length,
      listState.isListLoading,
    ]
  );
  const isZeroCount = useMemo(
    () =>
      Boolean(
        listRows.length === 0 &&
          listState.totalCount !== null &&
          !isValidApiCall
      ),
    [isValidApiCall, listRows.length, listState.totalCount]
  );
  const assignedCount = useMemo(() => activeRows.length, [activeRows]);
  const isFilterApplied = useMemo(
    () => listControls.searchText.trim().length > 0,
    [listControls.searchText]
  );

  // List management
  const listObserverElement = useInViewObserver(({ isInView }) => {
    setListState((prev) => ({ ...prev, isObserverInView: isInView }));
  });

  useEffect(() => {
    if (!listState.isObserverInView || !isValidApiCall || assignType === 2) {
      return;
    }

    Logger.log(
      'Fetching',
      listState.currentOffset,
      'to',
      listState.currentOffset + MAX_COUNT
    );

    setListState((prev) => ({
      ...prev,
      isListLoading: true,
      isListError: false,
    }));

    const ownerEncoding = getOwnersType(ownerType);
    const payload = isFullView
      ? {
          max: MAX_COUNT,
          offset: listState.currentOffset,
          payload: {
            wsearch: listControls.searchText,
            type: ownerEncoding,
          },
        }
      : {
          ...DEFAULT_PAGINATION,
          payload: { type: ownerEncoding },
        };

    fetchList(payload)
      .then((response) => {
        const { totalRecords, data } = response as {
          totalRecords: number;
          data: AccountOwnerListResponse[] | null | undefined;
        };

        if (!data) {
          setListState((prev) => ({ ...prev, totalCount: 0 }));

          return;
        }

        if (
          totalRecords > LIMIT_INLINE_COUNT &&
          currentMode === WRAPPER_MODE.INLINE
        ) {
          setInlineItemCount(data.length);
        }

        const trackedResponse = data.map((item) => {
          const id = getKeyValue(item, ownerEncoding);
          const isChecked =
            item.isChecked ||
            activeRows.find(
              (o) => o.tracker.type === ownerEncoding && o.tracker.id === id
            )?.tracker.isChecked;
          const rank = String(
            item.rank ??
              activeRows.find(
                (o) => o.tracker.type === ownerEncoding && o.tracker.id === id
              )?.tracker.rank ??
              '1'
          );

          return {
            ...item,
            tracker: {
              id,
              type: ownerEncoding,
              isChecked,
              rank,
            },
          };
        }) as TrackedListItem[];

        setListRows((prev) => [...prev, ...trackedResponse]);
        setListState((prev) => ({
          ...prev,
          totalCount: totalRecords,
          currentOffset: prev.currentOffset + MAX_COUNT,
        }));

        const checkedResponses = trackedResponse.filter(
          (i) => i.tracker.isChecked
        );

        setActiveRows((prev) => {
          const existingKeys = new Set(
            prev.map((row) => `${row.tracker.id}-${row.tracker.type}`)
          );

          const newRows = checkedResponses.filter((row) => {
            const key = `${row.tracker.id}-${row.tracker.type}`;

            return !existingKeys.has(key);
          });

          return [...prev, ...newRows];
        });
      })
      .catch((error) => {
        Logger.log('Received error while fetching: ', error);

        setListState((prev) => ({
          ...prev,
          isListError: true,
        }));
      })
      .finally(() => {
        setListState((prev) => ({
          ...prev,
          isListLoading: false,
          isObserverInView: false,
        }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Don't need to call on assignType change
  }, [
    isValidApiCall,
    fetchList,
    activeRows,
    isFullView,
    listState.isObserverInView,
    listState.currentOffset,
    listControls.searchText,
    ownerType,
    currentMode,
    setInlineItemCount,
  ]);

  // Resetting list and offset to default so that observer is visible
  useEffectAfterMount(() => {
    const debounceTimer = setTimeout(() => {
      setListState(DEFAULT_LIST_STATE);
      setListRows([]);
    }, 20);

    return () => clearTimeout(debounceTimer);
  }, [listControls.searchText, ownerType]);

  // Reset list when switching to assignType 1 to prevent duplicates
  useEffectAfterMount(() => {
    if (assignType === 1) {
      setListState(DEFAULT_LIST_STATE);
      setListRows([]);
    }
  }, [assignType]);

  // Setting selections
  useEffect(() => {
    setChecked(
      activeRows.map(({ tracker, ...body }) => ({
        ...body,
        isChecked: true,
        rank: Number(tracker.rank),
      }))
    );
  }, [activeRows, setChecked]);

  const handleClick = useCallback(
    (id: number, t: string) => {
      const modifiedList = listRows.map((item) => {
        if (item.tracker.id === id) {
          return {
            ...item,
            tracker: {
              ...item.tracker,
              isChecked: !item.tracker.isChecked,
            },
          };
        }

        return item;
      });

      setListRows(modifiedList);

      const currentItem = modifiedList.find(
        (e) => e.tracker.type === t && e.tracker.id === id
      );
      const targetRow = activeRows.find(
        (e) => e.tracker.type === t && e.tracker.id === id
      );

      if (targetRow && !currentItem?.tracker.isChecked) {
        setActiveRows((prev) =>
          prev.filter((e) => !(e.tracker.type === t && e.tracker.id === id))
        );

        return;
      }

      if (!targetRow && currentItem?.tracker.isChecked) {
        setActiveRows((prev) => [...prev, currentItem]);
      }
    },
    [activeRows, listRows]
  );

  const handleRankChange = useCallback(
    (id: number, t: string, rank: string) => {
      if (!isFullView) {
        setListRows((prev) =>
          prev.map((item) => {
            if (item.tracker.id === id && item.tracker.type === t) {
              return {
                ...item,
                tracker: {
                  ...item.tracker,
                  rank,
                },
              };
            }

            return item;
          })
        );
      }

      setActiveRows((prev) =>
        prev.map((item) => {
          if (item.tracker.id === id && item.tracker.type === t) {
            return {
              ...item,
              tracker: {
                ...item.tracker,
                rank,
              },
            };
          }

          return item;
        })
      );
    },
    [isFullView]
  );

  const handleRowClick = useCallback(
    (id: number, t: string) => {
      if (isFullView) {
        const activeIndex = focusedList.findIndex(
          (e) => e.tracker.type === t && e.tracker.id === id
        );

        if (activeIndex !== -1) {
          setSidePanelContext({
            activeIndex,
            activeItem: focusedList[activeIndex],
          });

          setIsSidePanelOpen(true);
        }
      }
    },
    [isFullView, focusedList]
  );

  const handleSearch = useCallback((text: string) => {
    setListControls((prev) => ({ ...prev, searchText: text }));
  }, []);

  // SidePanel utils
  const {
    headerTitle,
    headerDescription,
    headerIcon,
    isPreviousDisabled,
    isNextDisabled,
  } = useMemo(() => {
    const payload = {
      headerTitle: '',
      headerDescription: '',
      headerIcon: <ApplicationIcon icon='Account' size='xLarge' />,
      isPreviousDisabled: true,
      isNextDisabled: true,
    };

    // Swipe buttons state
    if (focusedList.length > 1) {
      if (sidePanelContext.activeIndex === 0) {
        payload.isPreviousDisabled = true;
        payload.isNextDisabled = false;
      }

      if (sidePanelContext.activeIndex === focusedList.length - 1) {
        payload.isPreviousDisabled = false;
        payload.isNextDisabled = true;
      }

      if (
        sidePanelContext.activeIndex !== null &&
        sidePanelContext.activeIndex !== -1 &&
        sidePanelContext.activeIndex > 0 &&
        sidePanelContext.activeIndex < focusedList.length - 1
      ) {
        payload.isPreviousDisabled = false;
        payload.isNextDisabled = false;
      }
    }

    // Header contents
    if (sidePanelContext.activeItem) {
      payload.headerTitle =
        sidePanelContext.activeItem.displayname ??
        sidePanelContext.activeItem.username;
    }

    if (sidePanelContext.activeItem?.tracker.type === OWNERS_TYPE.USER) {
      payload.headerDescription = 'User';
      payload.headerIcon = <ApplicationIcon icon='User' size='xLarge' />;
    }

    if (sidePanelContext.activeItem?.tracker.type === OWNERS_TYPE.USERGROUP) {
      payload.headerDescription = 'User Group';
      payload.headerIcon = <ApplicationIcon icon='Group' size='xLarge' />;
    }

    if (sidePanelContext.activeItem?.tracker.type === OWNERS_TYPE.MACHINE) {
      payload.headerDescription = 'Machine';
      payload.headerIcon = (
        <ApplicationIcon icon='MachineIdentity' size='xLarge' />
      );
    }

    return payload;
  }, [sidePanelContext, focusedList]);

  const handlePrevious = useCallback(() => {
    if (
      sidePanelContext.activeIndex !== null &&
      sidePanelContext.activeIndex !== -1
    ) {
      const newIndex = sidePanelContext.activeIndex - 1;

      if (newIndex >= 0) {
        setSidePanelContext({
          activeIndex: newIndex,
          activeItem: focusedList[newIndex],
        });
      }
    }
  }, [sidePanelContext, focusedList]);

  const handleNext = useCallback(() => {
    if (
      sidePanelContext.activeIndex !== null &&
      sidePanelContext.activeIndex !== -1
    ) {
      const newIndex = sidePanelContext.activeIndex + 1;

      if (newIndex < focusedList.length) {
        setSidePanelContext({
          activeIndex: newIndex,
          activeItem: focusedList[newIndex],
        });
      }
    }
  }, [sidePanelContext, focusedList]);

  const renderSidePanelBody = useCallback(() => {
    if (sidePanelContext.activeItem) {
      const { tracker, ...body } = sidePanelContext.activeItem;

      if (tracker.type === OWNERS_TYPE.USER) {
        const USER_FIELD_MAP = [
          { key: 'username', label: 'Username' },
          { key: 'email', label: 'Email' },
          { key: 'manager', label: 'Manager' },
          {
            key: 'manageremail',
            label: 'Manager Email',
          },
        ];

        const getTrimmedValue = (
          text: string | number | boolean | undefined | null
        ) => {
          if (!text) {
            return '-';
          }

          return text;
        };

        return USER_FIELD_MAP.map((f) => {
          const value = body[f.key as keyof typeof body];

          return (
            <Box
              tag='div'
              className='AccountOwners-SidePanelBody-listItem--isUser'>
              <Typography kind='h4' color={CF_NEU_BLD}>
                {f.label}
              </Typography>
              <Typography kind='body2' color={CF_SEC_BLD} lineLimit={1}>
                {!Array.isArray(value) ? (
                  getTrimmedValue(value)
                ) : (
                  <FormattedMessage
                    id='YoUJI1'
                    defaultMessage='-'
                    description='Hyphen'
                  />
                )}
              </Typography>
            </Box>
          );
        });
      }

      if (tracker.type === OWNERS_TYPE.USERGROUP) {
        return (
          <>
            <Box
              tag='div'
              className='AccountOwners-SidePanelBody-listItem--isUserGroup'>
              <Typography kind='h4' color={CF_NEU_BLD}>
                <FormattedMessage
                  id='3T09+Q'
                  defaultMessage='Description'
                  description='Description'
                />
              </Typography>
              <Typography kind='body2' color={CF_SEC_BLD}>
                {body.description ?? (
                  <FormattedMessage
                    id='YoUJI1'
                    defaultMessage='-'
                    description='Hyphen'
                  />
                )}
              </Typography>
            </Box>
            <Box
              tag='div'
              className='AccountOwners-SidePanelBody-listItem--isUserGroup'>
              <Typography kind='h4' color={CF_NEU_BLD}>
                <FormattedMessage
                  id='eFNwxW'
                  defaultMessage='Group Members ({userCount})'
                  description='Label for group members field in side panel of account owners, with user count in parentheses'
                  values={{ userCount: body.users.length }}
                />
              </Typography>
              <Box
                tag='div'
                className='AccountOwners-SidePanelBody-userSubItemWrapper'>
                {body.users.map((user) => (
                  <Box
                    tag='div'
                    className='AccountOwners-SidePanelBody-userSubItem'>
                    <Avatar size='xxSmall' />
                    <Typography kind='body2' color={CF_SEC_BLD}>
                      <FormattedMessage
                        id='EH/So1'
                        defaultMessage='{firstname} {lastname} ({username})'
                        description='first name, last name and username'
                        values={{
                          firstname: user.firstname ?? '',
                          lastname: user.lastname ?? '',
                          username: user.username,
                        }}
                      />
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </>
        );
      }

      return null;
    }

    return null;
  }, [sidePanelContext]);

  return (
    <>
      <Box
        tag='div'
        className='AccountOwners'
        dataTestId={testIds.LIST_VIEWER_ACCOUNT_OWNERS}>
        {isFullView && (
          <Box tag='div'>
            <Typography kind='h2' color={CF_SEC_BLD}>
              <FormattedMessage
                id='nIluxO'
                defaultMessage='Account Owners'
                description='Header for account owners section in list viewer'
              />
            </Typography>
            <Typography kind='body2' color={CF_NEU_MED}>
              <FormattedMessage
                id='BhYoor'
                defaultMessage='You can add up to {MAX_OWNER_COUNT} ranked owners during setup. After setup,
            additional owners can be added.'
                description='Header for account owners section in list viewer'
                values={{ MAX_OWNER_COUNT }}
              />
            </Typography>
          </Box>
        )}
        <Box tag='div' className='AccountOwners-togglers'>
          {isFullView ? (
            <ToggleButtonGroup
              selection={assignType}
              setSelection={setAssignType}
              isDisabled={listState.isListLoading}>
              <ToggleButton
                label='Available'
                size='medium'
                isDisabled={listState.isListLoading}
              />
              <ToggleButton
                label={`Assigned (${assignedCount})`}
                size='medium'
                isDisabled={listState.isListLoading || assignedCount === 0}
              />
            </ToggleButtonGroup>
          ) : (
            <>
              <Typography kind='h4' color={CF_NEU_BLD}>
                <FormattedMessage
                  id='ScS78g'
                  defaultMessage='Owner Type'
                  description='Label for owner type toggler in list viewer'
                />
              </Typography>
              <ToggleButtonGroup
                selection={ownerType}
                setSelection={setOwnerType}
                isDisabled={listState.isListLoading || assignType === 2}>
                <ToggleButton
                  label='User'
                  size='medium'
                  icon={<Icon kind='User' />}
                  isDisabled={listState.isListLoading || assignType === 2}
                />
                <ToggleButton
                  label='User Group'
                  size='medium'
                  icon={<Icon kind='Group' />}
                  isDisabled={listState.isListLoading || assignType === 2}
                />
                {/* <ToggleButton
                label='Machine'
                size='medium'
                icon={<Icon kind='MachineIdentity' />}
              /> */}
              </ToggleButtonGroup>
            </>
          )}
        </Box>
        {isFullView && (
          <Box tag='div' className='AccountOwners-controls'>
            <ListControls
              setSearchText={handleSearch}
              isDisabled={listState.isListLoading || assignType === 2}
            />
            <ToggleButtonGroup
              selection={ownerType}
              setSelection={setOwnerType}
              isDisabled={listState.isListLoading || assignType === 2}>
              <ToggleButton
                size='medium'
                icon={<Icon kind='User' />}
                isDisabled={listState.isListLoading || assignType === 2}
              />
              <ToggleButton
                size='medium'
                icon={<Icon kind='Group' />}
                isDisabled={listState.isListLoading || assignType === 2}
              />
              {/* <ToggleButton size='medium' icon={<Icon kind='MachineIdentity' />} /> */}
            </ToggleButtonGroup>
          </Box>
        )}
        <Box tag='div' className='AccountOwners-bodyWrapper'>
          {!isFullView && (
            <Typography kind='h4' color={CF_NEU_BLD}>
              <FormattedMessage
                id='Lxj+E/'
                defaultMessage='Available Owners'
                description='Header for available owners section in list viewer when not in full view'
              />
            </Typography>
          )}
          {assignType === 1 &&
            listRows.map((item) => (
              <AccountOwnerListItem
                isFullView={isFullView}
                key={`${item.tracker.id}-${item.tracker.type}-${assignType}`}
                listItem={item}
                isLoading={listState.isListLoading}
                isAssignedList={false}
                assignedCount={assignedCount}
                handleClick={handleClick}
                handleRowClick={handleRowClick}
                handleRankChange={handleRankChange}
              />
            ))}
          {assignType === 2 &&
            activeRows.map((item) => (
              <AccountOwnerListItem
                isFullView={isFullView}
                key={`${item.tracker.id}-${item.tracker.type}-${assignType}`}
                listItem={item}
                isLoading={listState.isListLoading}
                isAssignedList
                assignedCount={assignedCount}
                handleClick={handleClick}
                handleRowClick={handleRowClick}
                handleRankChange={handleRankChange}
              />
            ))}
          {listState.isListError && (
            <Box className='ListViewer-errorModal'>
              <Icon kind='StopProcess' size='large' color={CF_CRT_MED} />
              <Typography kind='body2' color={CF_CRT_MED}>
                <FormattedMessage
                  id='apz7lc'
                  defaultMessage='System encountered an error. Please contact the Support Administrator'
                  description='System encountered an error. Please contact the Support Administrator'
                />
              </Typography>
            </Box>
          )}
          {/* Observer Element */}
          {isFullView && !listState.isListError && !listState.isListLoading
            ? listObserverElement
            : null}
          {/* Loading Skeleton */}
          {!listState.isListError && listState.isListLoading && (
            <Box className='ListViewer-loadingModal'>
              <ListSkeleton count={3} />
            </Box>
          )}
          {!listState.isListError && isZeroCount && (
            <Box tag='div' className='AccessModal-illustrationWrapper'>
              <Illustration kind='Empty' />
              <Typography kind='h1'>
                {isFilterApplied ? (
                  <FormattedMessage
                    id='C0QjhC'
                    defaultMessage='No Results Found!'
                    description='No Results Found!'
                  />
                ) : (
                  <FormattedMessage
                    id='qHsPBM'
                    defaultMessage='There are no eligible users to select!'
                    description='Message to display when there are no account owners to display in list viewer and no filter is applied'
                  />
                )}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      <ListSidePanel
        isOpen={isSidePanelOpen}
        onClose={() => {
          setIsSidePanelOpen(false);
          setSidePanelContext(DEFAULT_SIDEPANEL_STATE);
        }}
        header={{
          title: headerTitle,
          description: headerDescription,
          headerIcon,
        }}
        layout={renderSidePanelBody()}
        handlePrevious={handlePrevious}
        handleNext={handleNext}
        isPreviousDisabled={isPreviousDisabled}
        isNextDisabled={isNextDisabled}
        className='AccountOwners-SidePanel'
        classNames={{ layoutClassName: 'AccountOwners-SidePanelLayout' }}
      />
    </>
  );
}

AccountOwners.defaultProps = {
  retentions: null,
};

export default memo(AccountOwners);
